import type { FlowGraphDataConnection } from "@babylonjs/core/FlowGraph/flowGraphDataConnection";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { Logger } from '../../utils/Logger';

/**
 * What a pick or hover block is aimed at when its target does not resolve.
 * `undefined` cannot stand in: Babylon's pick block fires for ANY picked mesh
 * when it has no target, and the hover blocks would match a pointer that
 * reports no mesh. The blocks only compare the target and walk `.parent` from
 * the mesh up to it, so this matches nothing.
 */
const NO_TARGET = Object.freeze({}) as unknown as AbstractMesh;

/**
 * The object a KHR_node_selectability / KHR_node_hoverability event listens
 * on, resolved again at each pointer event, not only when the graph compiles
 * (the compile-time look is what reports a path that names nothing). A target
 * can be created after the graph is (async glTF children, objects made in
 * play), and one that is deleted must stop the trigger, not move it to the
 * host.
 *
 * An empty path is the host. A non-empty path that names nothing makes the
 * trigger do nothing, like `resolveTarget` does for the `trace/*` ops.
 *
 * Babylon reads each block's target on every pointer event, and more than once
 * (it orders blocks by nesting), so the answer is cached against the scene's
 * `structureRevision` and walked again only after the scene changed shape.
 */
export class LiveEventTarget {
    private _object: SceneObject | null = null;
    /** NaN until resolved, and for a scene without a revision (test stubs): nothing is cached. */
    private _revision = NaN;
    private _reported = false;

    constructor(
        private readonly _path: string,
        private readonly _host: SceneObject,
        private readonly _traceScene: TraceScene,
    ) {}

    /** The object the target names right now; null when a non-empty path names nothing. */
    get object(): SceneObject | null {
        if (!this._path) return this._host;
        const rev = (this._traceScene as { structureRevision?: number }).structureRevision;
        const revision = typeof rev === 'number' ? rev : NaN;
        if (revision === this._revision && !this._object?.transform.isDisposed()) return this._object;
        this._revision = revision;
        this._object = this._traceScene.resolveSceneObject(this._path, this._host);
        if (!this._object && !this._reported) {
            this._reported = true;
            // ERROR, not WARN: Logger.level defaults to ERROR, so a warn here never
            // reaches anyone, and a broken reference must not be invisible.
            Logger.error(`[GraphCompiler] Event target path "${this._path}" could not be resolved — the trigger does nothing until an object is there`);
        }
        return this._object;
    }

    /** What the Babylon block matches a picked or hovered mesh against. */
    get mesh(): AbstractMesh {
        // Babylon types the target as AbstractMesh, but only compares it and
        // walks `.parent` (_IsDescendantOf), so a TransformNode works.
        return (this.object?.transform as AbstractMesh | undefined) ?? NO_TARGET;
    }

    /** Have `input` (a block's target input) read the target at each event, not the value the block was built with. */
    follow(input: FlowGraphDataConnection<AbstractMesh>): void {
        if (this._path) input.dataTransformer = () => this.mesh;
    }
}
